import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import type { ButtonType } from '../../lib/buttonType.ts';

export type MobileTabBarProps = Omit<React.ComponentProps<'nav'>, 'style'>;

/*
 * The rail's tiles along a phone's bottom edge, where a thumb reaches them.
 * It stands over the page, so pass it through the shell's `tabBar` slot: only
 * the shell can pad the page by the bar's height.
 */
export function MobileTabBar({ className, ...props }: MobileTabBarProps) {
  return (
    <nav
      data-slot='mobile-tab-bar'
      className={cn(
        'hidden max-mobile:flex fixed inset-x-0 bottom-0 z-20 items-stretch',
        'h-[calc(3.5rem+env(safe-area-inset-bottom,0px))] pb-[env(safe-area-inset-bottom,0px)]',
        'border-t border-line bg-canvas/92 px-1 backdrop-blur-sm',
        className,
      )}
      {...props}
    />
  );
}

/*
 * Variants rather than only a component: a tile is a link where it goes
 * straight to a page and a button where it opens a sheet, and the router's
 * Link is the consumer's to pass, as with railLinkVariants.
 */
export const mobileTabVariants = cva(
  [
    /*
     * Sized by its name plus an equal share of what is left, not a sixth each:
     * "Conversations" needs twice the width of "AI", and an equal split cuts it.
     */
    'relative flex min-w-0 flex-auto cursor-pointer flex-col items-center justify-center gap-1',
    'transition-colors',
    '[&_svg]:size-5 [&_svg]:shrink-0',
    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-faint',
    'before:absolute before:top-0 before:left-1/2 before:h-0.75 before:w-7 before:-translate-x-1/2',
    'before:rounded-b-xs before:content-[""]',
  ],
  {
    variants: {
      active: {
        true: 'text-ink before:bg-accent',
        false: 'text-ink-muted hover:text-ink before:bg-transparent',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

export type MobileTabVariants = VariantProps<typeof mobileTabVariants>;

export interface MobileTabProps
  extends Omit<React.ComponentProps<'button'>, 'style' | 'type'>, MobileTabVariants {
  type?: ButtonType;
}

/* A tile that opens something rather than going somewhere: the sheet of a group's pages. */
export function MobileTab({
  className,
  active,
  type = 'button',
  ...props
}: MobileTabProps) {
  return (
    <button
      data-slot='mobile-tab'
      type={type}
      className={cn(mobileTabVariants({ active }), className)}
      {...props}
    />
  );
}

export type MobileTabLabelProps = Omit<React.ComponentProps<'span'>, 'style'>;

/*
 * The name under a tile's icon. Like the rail's label it carries no accessible
 * name of its own: the tile holds an aria-label with the same words.
 */
export function MobileTabLabel({ className, ...props }: MobileTabLabelProps) {
  return (
    <span
      data-slot='mobile-tab-label'
      aria-hidden='true'
      className={cn(
        'max-w-full overflow-hidden text-2xs font-semibold text-ellipsis whitespace-nowrap',
        className,
      )}
      {...props}
    />
  );
}
